import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { getHttpClient, HttpClient, useHttpClient } from '@bonusly/http-client';
import { getQueryClient } from '@bonusly/query-client';

import { QueryKey as BonusFeedQueryKey } from '../modules/bonus-feed/features/recognition/api/hooks/types';
import { ExternalRecognitionLink, ExternalRecognitionSubmission, SubmissionState } from '../schemas/external-recognition';
import { ensureID, headers, PaginationParams, RecordOrID } from './meta';

enum QueryKey {
	Links = 'external-recognition:links',
	Submissions = 'external-recognition:submissions',
	PendingCount = 'external-recognition:pending-count',
}

export const externalRecognitionLinksQueryKey = () => [QueryKey.Links];
export const externalRecognitionLinkQueryKey = (id: string) => [...externalRecognitionLinksQueryKey(), id];
export const externalRecognitionSubmissionsQueryKey = () => [QueryKey.Submissions];
export const externalRecognitionPendingCountQueryKey = () => [QueryKey.PendingCount];

export const externalRecognitionLinksPath = (prefix = '') => `${prefix}/admin/external_recognition/links`;
export const externalRecognitionLinkPath = (id: string, prefix = '') => `${prefix}/admin/external_recognition/links/${id}`;
export const externalRecognitionPauseLinkPath = (id: string, prefix = '') => `${prefix}/admin/external_recognition/links/${id}/pause`;
export const externalRecognitionResumeLinkPath = (id: string, prefix = '') => `${prefix}/admin/external_recognition/links/${id}/resume`;
export const externalRecognitionSubmissionsPath = (prefix = '') => `${prefix}/admin/external_recognition/submissions`;
export const externalRecognitionApproveSubmissionPath = (id: string, prefix = '') => `${prefix}/admin/external_recognition/submissions/${id}/approve`;
export const externalRecognitionDenySubmissionPath = (id: string, prefix = '') => `${prefix}/admin/external_recognition/submissions/${id}/deny`;
export const externalRecognitionPendingCountPath = (prefix = '') => `${prefix}/admin/external_recognition/submissions/pending_count`;

export type SortDirection = 'asc' | 'desc';

export type PaginationMetadata = {
	pageNumber: number;
	pageSize: number;
	totalCount: number;
	totalPages: number;
};

// The backend index envelope. Keys arrive camelized via OliveBranch because the
// request carries the `Key-Inflection: camel` header.
type IndexResponse<T> = {
	records: T[];
	metadata?: PaginationMetadata;
};

const parseMetadata = (metadata: PaginationMetadata | undefined, recordCount: number): PaginationMetadata =>
	metadata
		? {
				pageNumber: metadata.pageNumber,
				pageSize: metadata.pageSize,
				totalCount: metadata.totalCount,
				totalPages: metadata.totalPages,
			}
		: { pageNumber: 1, pageSize: recordCount, totalCount: recordCount, totalPages: 1 };

/*
 * Links
 */

// `search` filters links by their `name` field on the backend; case-insensitive
// contains. Omitted/blank returns all links.
export type ExternalRecognitionLinksFilters = PaginationParams<{ search?: string }>;

export type ExternalRecognitionLinksResult = {
	records: ExternalRecognitionLink[];
	metadata: PaginationMetadata;
};

export const useExternalRecognitionLinks = (filters: ExternalRecognitionLinksFilters = {}, options?: { enabled?: boolean }) => {
	const http = useHttpClient();

	return useQuery<ExternalRecognitionLinksResult>({
		queryKey: [...externalRecognitionLinksQueryKey(), filters],
		queryFn: async () => {
			const res = await http.get<IndexResponse<unknown>>(externalRecognitionLinksPath(), { headers, params: filters });
			// Per-record safeParse so one malformed link can't blank the whole
			// admin table (mirrors the PAT index's resilience posture).
			const records = res.data.records.flatMap((record) => {
				const parsed = ExternalRecognitionLink.safeParse(record);
				return parsed.success ? [parsed.data] : [];
			});
			return { records, metadata: parseMetadata(res.data.metadata, records.length) };
		},
		enabled: options?.enabled,
		keepPreviousData: true,
	});
};

const createLinkFetcher = (http: HttpClient, id: string) => async (): Promise<ExternalRecognitionLink> => {
	const res = await http.get<unknown>(externalRecognitionLinkPath(id), { headers });
	return ExternalRecognitionLink.parse(res.data);
};

export const useExternalRecognitionLink = (id: string) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: externalRecognitionLinkQueryKey(id),
		queryFn: createLinkFetcher(http, id),
		enabled: !!id,
	});
};

export const loadExternalRecognitionLink = async (id: string) => {
	const http = await getHttpClient();
	const queryClient = await getQueryClient();

	return queryClient.fetchQuery({
		queryKey: externalRecognitionLinkQueryKey(id),
		queryFn: createLinkFetcher(http, id),
	});
};

// Mutation payload for create/update.
export type LinkMutationPayload = {
	recipientRuleConditions: { attr: string; value: string }[];
	name: string;
	startDate: string;
	endDate?: string | null;
	message?: string | null;
	includeCompanyValueHashtags?: boolean;
	requireSubmitterName?: boolean;
};

type MutateOptions<R> = {
	onError?: (error: unknown) => void;
	onMutate?: () => void;
	onSuccess?: (record: R) => void;
};

const buildLinkBody = (payload: LinkMutationPayload): Record<string, unknown> => ({
	recipient_rule_conditions: payload.recipientRuleConditions,
	name: payload.name,
	start_date: payload.startDate,
	end_date: payload.endDate || null,
	message: payload.message ?? '',
	include_company_value_hashtags: payload.includeCompanyValueHashtags ?? true,
	require_submitter_name: payload.requireSubmitterName ?? false,
});

export const useCreateLink = (options: MutateOptions<ExternalRecognitionLink> = {}) => {
	const http = useHttpClient();
	const invalidate = useInvalidateExternalRecognitionLinks();

	return useMutation({
		mutationFn: async (payload: LinkMutationPayload) => {
			try {
				options.onMutate?.();
				const res = await http.post<unknown>(externalRecognitionLinksPath(), buildLinkBody(payload), { headers });
				const record = ExternalRecognitionLink.parse(res.data);
				invalidate();
				options.onSuccess?.(record);
				return record;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

export const useUpdateLink = (recordOrId: RecordOrID, options: MutateOptions<ExternalRecognitionLink> = {}) => {
	const http = useHttpClient();
	const invalidate = useInvalidateExternalRecognitionLinks();

	return useMutation({
		mutationFn: async (payload: LinkMutationPayload) => {
			try {
				options.onMutate?.();
				const res = await http.put<unknown>(externalRecognitionLinkPath(ensureID(recordOrId)), buildLinkBody(payload), { headers });
				const record = ExternalRecognitionLink.parse(res.data);
				invalidate();
				options.onSuccess?.(record);
				return record;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

export const useDeleteLink = (options: MutateOptions<void> = {}) => {
	const http = useHttpClient();
	const invalidate = useInvalidateExternalRecognitionLinks();

	return useMutation({
		mutationFn: async (recordOrId: RecordOrID) => {
			await http.delete(externalRecognitionLinkPath(ensureID(recordOrId)), { headers });
		},
		onSuccess: () => {
			invalidate();
			options.onSuccess?.(undefined as void);
		},
		onError: (e) => {
			options.onError?.(e);
		},
	});
};

export const usePauseLink = (options: MutateOptions<ExternalRecognitionLink> = {}) => {
	const http = useHttpClient();
	const invalidate = useInvalidateExternalRecognitionLinks();

	return useMutation({
		mutationFn: async (recordOrId: RecordOrID) => {
			const res = await http.patch<unknown>(externalRecognitionPauseLinkPath(ensureID(recordOrId)), {}, { headers });
			return ExternalRecognitionLink.parse(res.data);
		},
		onSuccess: (record) => {
			invalidate();
			options.onSuccess?.(record);
		},
		onError: (e) => {
			options.onError?.(e);
		},
	});
};

export const useResumeLink = (options: MutateOptions<ExternalRecognitionLink> = {}) => {
	const http = useHttpClient();
	const invalidate = useInvalidateExternalRecognitionLinks();

	return useMutation({
		mutationFn: async (recordOrId: RecordOrID) => {
			const res = await http.patch<unknown>(externalRecognitionResumeLinkPath(ensureID(recordOrId)), {}, { headers });
			return ExternalRecognitionLink.parse(res.data);
		},
		onSuccess: (record) => {
			invalidate();
			options.onSuccess?.(record);
		},
		onError: (e) => {
			options.onError?.(e);
		},
	});
};

export const useInvalidateExternalRecognitionLinks = () => {
	const client = useQueryClient();
	return useCallback(() => {
		client.invalidateQueries(externalRecognitionLinksQueryKey());
	}, [client]);
};

export type BulkLinkActionResult = { succeeded: string[]; failed: string[] };

const settleBulk = async (ids: string[], request: (id: string) => Promise<unknown>): Promise<BulkLinkActionResult> => {
	const settled = await Promise.allSettled(ids.map((id) => request(id)));
	const succeeded: string[] = [];
	const failed: string[] = [];
	settled.forEach((result, index) => (result.status === 'fulfilled' ? succeeded : failed).push(ids[index]));
	return { succeeded, failed };
};

export const useBulkPauseLinks = () => {
	const http = useHttpClient();
	const invalidate = useInvalidateExternalRecognitionLinks();

	return useMutation({
		mutationFn: (ids: string[]) => settleBulk(ids, (id) => http.patch(externalRecognitionPauseLinkPath(id), {}, { headers })),
		onSuccess: invalidate,
	});
};

export const useBulkDeleteLinks = () => {
	const http = useHttpClient();
	const invalidate = useInvalidateExternalRecognitionLinks();

	return useMutation({
		mutationFn: (ids: string[]) => settleBulk(ids, (id) => http.delete(externalRecognitionLinkPath(id), { headers })),
		onSuccess: invalidate,
	});
};

/*
 * Submissions
 */

export type ExternalRecognitionSubmissionsFilters = PaginationParams<{ state?: SubmissionState }>;

export type ExternalRecognitionSubmissionsResult = {
	records: ExternalRecognitionSubmission[];
	metadata: PaginationMetadata;
};

export const useSubmissions = (filters: ExternalRecognitionSubmissionsFilters = {}, options?: { enabled?: boolean }) => {
	const http = useHttpClient();

	return useQuery<ExternalRecognitionSubmissionsResult>({
		queryKey: [...externalRecognitionSubmissionsQueryKey(), filters],
		queryFn: async () => {
			const res = await http.get<IndexResponse<unknown>>(externalRecognitionSubmissionsPath(), { headers, params: filters });
			const records = res.data.records.flatMap((record) => {
				const parsed = ExternalRecognitionSubmission.safeParse(record);
				return parsed.success ? [parsed.data] : [];
			});
			return { records, metadata: parseMetadata(res.data.metadata, records.length) };
		},
		enabled: options?.enabled,
		keepPreviousData: true,
		refetchOnMount: 'always',
	});
};

export const useInvalidateSubmissions = () => {
	const client = useQueryClient();
	return useCallback(() => {
		// Approving/denying changes both the row and the pending-count badge.
		client.invalidateQueries(externalRecognitionSubmissionsQueryKey());
		client.invalidateQueries(externalRecognitionPendingCountQueryKey());
		// Clear the bonus feed cache to surface the new recognition post.
		client.invalidateQueries([BonusFeedQueryKey.BonusesInfinite]);
	}, [client]);
};

export const useApproveSubmission = (recordOrId: RecordOrID, options: MutateOptions<ExternalRecognitionSubmission> = {}) => {
	const http = useHttpClient();
	const invalidate = useInvalidateSubmissions();

	return useMutation({
		mutationFn: async () => {
			try {
				options.onMutate?.();
				const res = await http.post<unknown>(externalRecognitionApproveSubmissionPath(ensureID(recordOrId)), {}, { headers });
				const record = ExternalRecognitionSubmission.parse(res.data);
				invalidate();
				options.onSuccess?.(record);
				return record;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

export const useDenySubmission = (recordOrId: RecordOrID, options: MutateOptions<ExternalRecognitionSubmission> = {}) => {
	const http = useHttpClient();
	const invalidate = useInvalidateSubmissions();

	return useMutation({
		mutationFn: async () => {
			try {
				options.onMutate?.();
				const res = await http.post<unknown>(externalRecognitionDenySubmissionPath(ensureID(recordOrId)), {}, { headers });
				const record = ExternalRecognitionSubmission.parse(res.data);
				invalidate();
				options.onSuccess?.(record);
				return record;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

export const useBulkApproveSubmissions = () => {
	const http = useHttpClient();
	const invalidate = useInvalidateSubmissions();

	return useMutation({
		mutationFn: (ids: string[]) => settleBulk(ids, (id) => http.post(externalRecognitionApproveSubmissionPath(id), {}, { headers })),
		onSuccess: invalidate,
	});
};

export const useBulkDenySubmissions = () => {
	const http = useHttpClient();
	const invalidate = useInvalidateSubmissions();

	return useMutation({
		mutationFn: (ids: string[]) => settleBulk(ids, (id) => http.post(externalRecognitionDenySubmissionPath(id), {}, { headers })),
		onSuccess: invalidate,
	});
};

/*
 * Pending-approval count (sidebar badge)
 */

// `{ needs_approval_count: N }` arrives camelized as `needsApprovalCount`.
type PendingCountResponse = { needsApprovalCount: number };

export const usePendingSubmissionsCount = (options?: { enabled?: boolean }): number => {
	const http = useHttpClient();

	const { data } = useQuery({
		queryKey: externalRecognitionPendingCountQueryKey(),
		queryFn: async () => {
			const res = await http.get<PendingCountResponse>(externalRecognitionPendingCountPath(), { headers });
			return res.data.needsApprovalCount ?? 0;
		},
		enabled: options?.enabled,
		refetchOnMount: 'always',
	});

	return data ?? 0;
};
